import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  useAdminGetUserQuery,
  useUpdateUserMutation,
} from "../../services/organization/adminUsers";
import Avatar from "../../components/inputs/Avatar";
import Switch from "../../components/inputs/Switch";
import About from "../../components/Profile/Tabs/About";
import Cert from "../../components/Profile/Tabs/Cert";
import Exams from "../../components/Profile/Tabs/Exams";
import LearningLevel from "../../components/Profile/Tabs/LearningLevel";
import Posts from "../../components/Profile/Tabs/Posts";
import Reports from "../../components/Profile/Tabs/Reports";
import TabsNav from "../../components/tabs/Tabs";
import { getAccountStatus } from "../../utils/helpers";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useTranslation } from "react-i18next";
import NotFoundPage from "../404";

const TABS = {
  ABOUT: 1,
  REPORTS: 2,
  LEARNINGLEVEL: 3,
  CERT: 4,
  POSTS: 5,
  EXAMS: 6,
};

export default function AdminEmployeeProfile() {
  const [accountStatusType, setAccountStatusType] = useState();
  const [accountStatusName, setAccountStatusName] = useState();
  const [currentTab, setCurrentTab] = useState(TABS.ABOUT);

  const { t } = useTranslation([
    "common",
    "employees",
    "communityFeed",
    "learningPaths",
  ]);

  const { employeeId } = useParams();
  const { data: userData, isLoading: isGetUserLoading } =
    useAdminGetUserQuery(employeeId);
  const [updateUser] = useUpdateUserMutation();

  useEffect(() => {
    if (userData?.data) {
      const accountStatus = getAccountStatus(
        userData?.data?.registered,
        userData?.data?.active,
        t,
      );
      setAccountStatusType(accountStatus?.type);
      setAccountStatusName(accountStatus?.name);
    }
  }, [userData, t]);

  if (isGetUserLoading) return <LoadingIndicator />;

  if (!userData) {
    return <NotFoundPage />;
  }

  const user = userData?.data;

  return (
    <div className="admin-employee-profile">
      <div className="sticky-header header-table no-padding-b admin-employee-profile__header">
        <div className="admin-employee-profile__toolbar">
          <div className="admin-employee-profile__header-card">
            <div className="admin-employee-profile__identity">
              <Avatar width="60" height="60" avatarUrl={user?.avatar_url} />
              <div className="admin-employee-profile__identity-text">
                <p className="admin-employee-profile__name font_18 font-extrabold mb-0">
                  {user?.name}
                </p>
                {user?.department?.name ? (
                  <p className="admin-employee-profile__department font_14 font-color-grey mb-0">
                    {user?.department?.name}
                  </p>
                ) : null}
                <p className="admin-employee-profile__email font_12 font-color-grey mb-0">
                  {user?.email}
                </p>
              </div>
            </div>

            <div className="admin-employee-profile__status">
              <Switch
                label={t(accountStatusName)}
                disabled={accountStatusType == "notResigtered"}
                checked={accountStatusType == "active"}
                onChange={() =>
                  updateUser({
                    userId: user?.id,
                    updates: {
                      active: !(accountStatusType == "active"),
                    },
                  })
                }
              />
            </div>
          </div>
        </div>

        <TabsNav
          activeIndex={currentTab - 1}
          tabsConfig={[
            {
              name: "about",
              label: t("employees:aboutEmployee"),
              mobileLabel: t("employees:aboutEmployeeShort"),
              clickHandler: () => setCurrentTab(TABS.ABOUT),
            },
            {
              name: "reports",
              label: t("reports"),
              mobileLabel: t("employees:reportsShort"),
              clickHandler: () => setCurrentTab(TABS.REPORTS),
            },
            {
              name: "learning-level",
              label: t("learningPaths:learningLevel"),
              mobileLabel: t("employees:learningLevelShort"),
              clickHandler: () => setCurrentTab(TABS.LEARNINGLEVEL),
            },
            {
              name: "certificates",
              label: t("certificates"),
              mobileLabel: t("employees:certificatesShort"),
              clickHandler: () => setCurrentTab(TABS.CERT),
            },
            {
              name: "posts",
              label: t("communityFeed:thePost"),
              mobileLabel: t("employees:postsShort"),
              clickHandler: () => setCurrentTab(TABS.POSTS),
            },
            {
              name: "exams",
              label: t("exams"),
              mobileLabel: t("employees:examsShort"),
              clickHandler: () => setCurrentTab(TABS.EXAMS),
            },
          ]}
        />
      </div>

      <div className="content-page__wrapper admin-employee-profile__body">
        <div className="tab-content" id="nav-tabContent">
          {currentTab === TABS.ABOUT && <About user={user} />}
          {currentTab === TABS.REPORTS && <Reports user={user} />}
          {currentTab === TABS.LEARNINGLEVEL && <LearningLevel user={user} />}
          {currentTab === TABS.CERT && <Cert user={user} />}
          {currentTab === TABS.POSTS && <Posts user={user} />}
          {currentTab === TABS.EXAMS && <Exams user={user} />}
        </div>
      </div>
    </div>
  );
}
